import { QueryObserverResult, useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useGlobalStore } from '../../store/global-store/global-store';
import { InitiativeTasksConfig } from '../initiative-client/initiative-client.type';
import type { ExtendedEpic, MinifiedTask, SimpleEpic, StrategicEpic, Task } from './task-client.type';
import { exportTasksAsCsv, getEpic, getEpicsForProjects, getEpicTasks, searchTasks } from './tasks-client';

/**
 * Hook for exporting tasks as CSV
 *
 * @returns Mutation for handling CSV export of task IDs
 */
function useExportTasks() {
  return useMutation<Blob, Error, string[]>({
    mutationFn: exportTasksAsCsv,
  });
}

/**
 * Hook for searching tasks
 *
 * @param {string} query - The search query
 * @param {string | null} projectId - The project ID
 * @param {string | null} boardId - The board ID
 * @param {string | null} workflowId - The workflow ID
 * @param {string | null} taskType - The task type
 * @param {string | null} startDate - The start date
 * @param {string | null} endDate - The end date
 * @param {UseQueryOptions<Task[]>} options - Options for the useQuery hook
 * @return {{ tasks: Task[]; query: QueryObserverResult<Task[]> }} Object containing tasks and query result
 */
const useTaskSearch = (
  query: string,
  projectId?: string | null,
  boardId?: string | null,
  workflowId?: string | null,
  taskType?: string | null,
  startDate?: string | null,
  endDate?: string | null,
  options?: UseQueryOptions<Task[]>,
) => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const enabled = !!query && !!organizationId && (!!projectId || !!boardId);

  const queryResult = useQuery<Task[]>({
    queryKey: ['task-search', query, organizationId, projectId, boardId, workflowId, taskType, startDate, endDate],
    queryFn: () =>
      organizationId
        ? searchTasks(query, organizationId, projectId, boardId, workflowId, taskType, startDate, endDate)
        : Promise.reject('Organization ID required'),
    enabled,
    ...options,
  });

  return {
    tasks: queryResult.data || [],
    query: queryResult,
  };
};

/**
 * Hook for fetching simple epics
 *
 * @param {UseQueryOptions<SimpleEpic[]>} options - Options for the useQuery hook
 * @return {{ epics: SimpleEpic[] | undefined; query: QueryObserverResult<SimpleEpic[]> }} Object containing epics and query result
 */
const useSimpleEpics = (options?: UseQueryOptions<SimpleEpic[]>) => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const query = useQuery<SimpleEpic[]>({
    queryKey: ['epics', portfolio?.id],
    queryFn: () => getEpicsForProjects(portfolio?.id),
    enabled: !!portfolio?.id,
    ...options,
  });
  return { epics: query.data, query };
};

/**
 * Hook for fetching strategic epics
 *
 * @param {UseQueryOptions<StrategicEpic[]>} options - Options for the useQuery hook
 * @return {{ epics: StrategicEpic[] | undefined; query: QueryObserverResult<StrategicEpic[]> }} Object containing epics and query result
 */
const useStrategicEpics = (options?: UseQueryOptions<StrategicEpic[]>) => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const query = useQuery<StrategicEpic[]>({
    queryKey: ['epics', portfolio?.id],
    queryFn: () => getEpicsForProjects(portfolio?.id) as unknown as Promise<StrategicEpic[]>,
    enabled: !!portfolio?.id,
    ...options,
  });
  return { epics: query.data, query };
};

/**
 * Hook for fetching an epic
 *
 * @param {string | undefined} epicId - The epic ID
 * @param {UseQueryOptions<ExtendedEpic>} options - Options for the useQuery hook
 * @return {{ epic: ExtendedEpic | undefined; query: QueryObserverResult<ExtendedEpic> }} Object containing epic and query result
 */
const useEpic = (epicId: string | undefined, options?: UseQueryOptions<ExtendedEpic>) => {
  const query = useQuery<ExtendedEpic>({
    queryKey: ['epic', epicId],
    queryFn: () => getEpic(epicId as string),
    enabled: !!epicId,
    ...options,
  });
  return { epic: query.data, query };
};

/**
 * Hook for fetching epic tasks
 *
 * @param {string | undefined} epicId - The epic ID
 * @param {InitiativeTasksConfig} filters - Filters to apply to the task query
 * @param {Partial<UseQueryOptions<MinifiedTask[]>>} options - Options for the useQuery hook
 * @return {{ tasks: MinifiedTask[] | undefined; query: QueryObserverResult<MinifiedTask[]> }} Object containing tasks and query result
 */
const useEpicTasks = (
  epicId: string | undefined,
  filters: InitiativeTasksConfig,
  options?: Partial<UseQueryOptions<MinifiedTask[]>>,
): { tasks: MinifiedTask[] | undefined; query: QueryObserverResult<MinifiedTask[]> } => {
  const query = useQuery<MinifiedTask[]>({
    queryKey: ['epic-tasks', epicId, filters.filter, filters.date, filters, filters.projectId],
    queryFn: () => getEpicTasks(epicId as string, filters),
    enabled: !!epicId && !!filters.filter && !!filters.date,
    ...options,
  });
  return { tasks: query.data, query };
};

export { useEpic, useEpicTasks, useExportTasks, useSimpleEpics, useStrategicEpics, useTaskSearch };
